"use client";

import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { LinkTrackers } from "~/components/reusables/link-trackers";
import { Button } from "../ui/button";
import { ArrowLongRight } from "../icons";

export interface SlideData {
  id: number;
  title: string;
  description: string;
  image: string;
  authors: { name: string; role: string; avatar: string }[];
  link: string;
  category: string;
}

export default function GsapResearchSlider({ otherResource }: { otherResource: SlideData[] }) {    
  const [currentIndex, setCurrentIndex] = useState(0);
  const componentRef = useRef<HTMLDivElement>(null);
  
  const currentSlide = otherResource?.[currentIndex];
  const hasMultipleSlides = otherResource?.length > 1;

  useLayoutEffect(() => {
    if (!currentSlide || !componentRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(".animate-text", 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 0.8, ease: "power3.out" }
      );
      
      gsap.fromTo(".animate-image",
        { scale: 1.2, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.5, ease: "expo.out" }
      );
    }, componentRef);

    return () => ctx.revert();
  }, [currentIndex, currentSlide]);

  const handleNavigation = useCallback((direction: "next" | "prev") => {
    if (!hasMultipleSlides) return;

    const timeline = gsap.timeline({
      onComplete: () => {
        if (direction === "next") {
          setCurrentIndex((prev) => (prev + 1) % otherResource.length);
        } else {
          setCurrentIndex((prev) => (prev - 1 + otherResource.length) % otherResource.length);
        }
      }
    });

    timeline.to(".animate-text", { y: -20, opacity: 0, stagger: 0.05, duration: 0.3 });
    timeline.to(".animate-image", { opacity: 0, scale: 0.95, duration: 0.3 }, "<");
  }, [hasMultipleSlides, otherResource?.length]);

  useEffect(() => {
    if (!hasMultipleSlides) return;
    const autoPlayTimer = setTimeout(() => handleNavigation("next"), 5000);
    return () => clearTimeout(autoPlayTimer);
  }, [currentIndex, hasMultipleSlides, handleNavigation]);

  if (!currentSlide) {
    return <div className="min-h-[650px] bg-black animate-pulse" />;
  }

  return (
   <section className="w-full bg-black overflow-hidden">
      <div className="container mx-auto relative px-0 md:px-6 lg:px-8">
        
        <div 
          ref={componentRef}
          className="flex flex-col-reverse lg:flex-row w-full min-h-[650px] h-auto"
        >
          {/* --- LEFT SIDE: IMAGE --- */}
          <div className="relative w-full lg:w-1/2 h-[350px] sm:h-[450px] lg:h-auto px-6 lg:px-16 py-6 lg:py-0 overflow-hidden flex items-center bg-[#050511]">
            <div className="animate-image relative w-full h-full min-h-[300px]">
              <Image
                src={currentSlide.image}
                alt={currentSlide.title}
                fill
                priority
                className="object-contain"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/60 lg:from-black/40 to-transparent pointer-events-none" />
          </div>

          {/* --- RIGHT SIDE: CONTENT --- */}
          <div className="w-full lg:w-1/2 flex flex-col justify-start items-start p-6 sm:p-10 lg:px-16 lg:py-12 relative bg-gradient-to-br from-[#0D274D] via-[#00142B] to-black">
            <div className="max-w-xl w-full space-y-6 lg:space-y-8">
              <span className="animate-text block font-normal uppercase text-sm text-white">
                {currentSlide.category || "Whitepaper"}
              </span>

              <h2 className="animate-text text-3xl md:text-5xl lg:text-6xl font-light text-white leading-[1.1] tracking-tight">
                {currentSlide.title}
              </h2>

              <p className="animate-text text-base md:text-lg text-white/70 font-light leading-relaxed line-clamp-4">
                {currentSlide.description}
              </p>

              {/* Authors Section */}
              <div className="animate-text flex flex-col gap-4 pt-2">
                <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] font-bold">Authors</p>
                <div className="flex flex-wrap gap-4">
                  {currentSlide.authors.map((author, i) => (
                    <div key={`${author.name}-${i}`} className="flex items-center gap-3">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border border-white/10">
                        <Image src={author.avatar || "https://outshift-headless-cms-s3.s3.us-east-2.amazonaws.com/logo-round.svg"} alt={author.name} fill className="object-cover" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium leading-none">{author.name}</p>
                        <p className="text-white text-[10px] mt-1 uppercase tracking-wider">{author.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-text pt-4">
                <Button variant="outline" asChild className="py-6 text-black bg-white hover:bg-neutral-100 border-none transition-all hover:shadow-white">
                  <LinkTrackers
                    href={currentSlide.link}
                    segmentMsg="Slider CTA Clicked"
                    segmentOpt={{ title: currentSlide.title, category: currentSlide.category }}
                    className="flex items-center gap-2 px-6 py-3"
                    target="_self"
                  >
                    <span className="font-medium">Read Full Paper</span>
                    <ArrowLongRight size={24} />
                  </LinkTrackers>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {hasMultipleSlides && (
          <div className="relative lg:absolute bottom-0 lg:bottom-8 right-0 lg:right-8 flex justify-center lg:justify-end items-center py-8 lg:py-0 w-full lg:w-auto z-30 bg-black lg:bg-transparent">
            <div className="flex gap-4">
              <button 
                onClick={() => handleNavigation("prev")}
                className="p-4 border border-white/20 rounded-full text-white hover:bg-white hover:text-black transition-all backdrop-blur-md bg-white/5"
                aria-label="Previous slide"
              >
                <ArrowLeft size={16} />
              </button>
              <button 
                onClick={() => handleNavigation("next")}
                className="p-4 border border-white/20 rounded-full text-white hover:bg-white hover:text-black transition-all backdrop-blur-md bg-white/5"
                aria-label="Next slide"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}